import React, { useEffect, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import i18n from 'i18n';
import Button from 'react-bootstrap/Button';
import FilterBar from '../../../FilterBar/FilterBar';
import { Filter } from '../../../../../store/mockServices/mockServiceTypes';
import AssignModalMockServicesTable from './AssignModalMockServicesTable';
import { MockService } from '../../../../../types/rest/mockServiceTypes';
import acApi from '../../../../../axios/ac/acApi';
import _ from 'lodash';
import { useIsMount } from '../../../../../hooks/useIsMountHook';
import { Col, Row } from 'react-bootstrap';
import BzmIcon from '../../../../../components/icons/BzmIcon';
import { getSelectedCountTitle } from '../../../../../utils/miscUtil';
import { getActiveWorkspaceId } from '../../../../../utils/bzmUserConfigUtil';
import { buildAnalyticsQueryForHits, updateMockServiceListWithHits } from '../../../../../utils/mockServiceUtils';
import { Metrics } from '../../../../../types/rest/analyticsTypes';
import { getHitAndMissesFromAnalytics } from '../../../../../store/analytics/data/analyticsDataActions';
import {
  MockServiceGroup,
  ServiceMockGroupAssignment,
} from '../../../../../store/mockServiceGroups/mockServiceGroupTypes';
import { createSlug } from '../../../../../utils/urlUtils';
import { useDispatch } from 'react-redux';
import { updateMockServiceGroupPatch } from '../../../../../store/mockServiceGroups/mockServiceGroupsActions';
import MixpanelService from '../../../../../utils/mixpanelService';
import { ASSIGN_MOCK_SERVICE_TO_GROUP } from '../../../../../utils/mixpanelEvents';

interface AssignMockServiceModalProps {
  onHide: () => void;
  onAssignmentFinished: (msCount: number) => void;
  group: MockServiceGroup;
}

const AssignMockServiceModal = (props: AssignMockServiceModalProps) => {
  const [filter, setFilter] = useState<Filter>({ status: null, search: null, location: null, shipId: null });
  const isMount = useIsMount();
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [selected, setSelected] = useState<MockService[]>([]);
  const [mockServices, setMockServices] = useState<MockService[]>([]);

  useEffect(() => {
    if (isMount) {
      //Load virtual services on mount
      getMockServices();
    }
  }, []);

  const getAnalytics = async (mockServices: MockService[]) => {
    //Get active workspace id
    const workSpaceId = getActiveWorkspaceId();
    const analyticsQuery = buildAnalyticsQueryForHits(mockServices, workSpaceId);
    const results: Metrics[] = await getHitAndMissesFromAnalytics(analyticsQuery);

    //Update mocks with analytics data
    const mocks = updateMockServiceListWithHits(mockServices, results);
    setMockServices(mocks);
  };

  const getMockServices = async () => {
    setIsLoading(true);
    //Get only virtual services with same location (shipId) as the group
    const shipId = _.get(props.group, 'shipId');
    const params = {
      shipIds: shipId,
    };
    const res = (await acApi.mockServices.getAll(params)).data.result;

    if (!_.isNil(res)) {
      const mocks = _.map(res, item => {
        return { ...item, metaData: { isDeployed: false } };
      }).filter((sm: MockService) => {
        //Filter out already assigned virtual services
        if (_.size(props.group.serviceMocks) === 0) {
          return true;
        }
        const index = _.findIndex(props.group.serviceMocks, item => item.serviceMockId === sm.id);
        return index === -1;
      });
      setMockServices(mocks);
      getAnalytics(mocks);
    }
    setIsLoading(false);
  };

  const selectedCount = getSelectedCountTitle(_.size(mockServices), _.size(selected));

  const onAssignHandler = async () => {
    MixpanelService.track(ASSIGN_MOCK_SERVICE_TO_GROUP());

    //Assign new virtual services to the mock group and trigger update
    const group = _.cloneDeep(props.group);
    const smAssignments: ServiceMockGroupAssignment[] = _.map(selected, mockService => {
      return { serviceMockId: mockService.id, basePath: '/' + createSlug(mockService.name) };
    });

    group.serviceMocks = _.concat(group.serviceMocks, smAssignments);
    await dispatch(updateMockServiceGroupPatch(group, ['serviceMocks']));
    props.onAssignmentFinished(_.size(selected));
  };

  return (
    <Modal
      show={true}
      centered={true}
      backdrop={true}
      onHide={props.onHide}
      size="xl"
      dialogClassName={'modal-full-screen'}
    >
      <Modal.Header closeButton>
        <h5 className="modal-title">Assign Virtual Services to Virtual Service Group</h5>
      </Modal.Header>
      <Modal.Body>
        <div className={'pt-1'}>
          <FilterBar
            config={{ locationFilter: false, serviceFilter: true, statusFilter: true, searchFilter: true }}
            searchContext={'mockServices'}
            filter={filter}
            onChange={filter => {
              setFilter(filter);
            }}
            isGlobalServiceFilter={true}
          />
        </div>

        <Row className={'mb-3'}>
          <Col md={6}>
            <h4 className={'mb-2'}>VIRTUAL SERVICES {selectedCount}</h4>
            <p>Select the Virtual Services to include in the group and click Assign.</p>
          </Col>
          <Col md={6} className={'text-right'}>
            <button
              className={'btn btn-secondary ml-4'}
              onClick={() => {
                getMockServices();
              }}
            >
              <BzmIcon variant={'icon-bzm-sync'} className={'pr-2'} /> Refresh
            </button>
          </Col>
        </Row>
        <AssignModalMockServicesTable
          onSelect={(selectedItems, selectAll) => {
            setSelected(selectedItems);
          }}
          filter={filter}
          mockServices={mockServices}
          isLoading={isLoading}
        />
      </Modal.Body>
      <Modal.Footer>
        <Button id={'qa-assign-ms-modal-btn-cancel'} variant="link" onClick={props.onHide}>
          {i18n.t('common:actions.cancel')}
        </Button>

        <Button
          id={'qa-assign-ms-modal-btn-assign'}
          variant="primary"
          onClick={onAssignHandler}
          disabled={_.size(selected) === 0}
        >
          {i18n.t('groups:modal.assign')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AssignMockServiceModal;
